import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Button from 'antd/lib/button';
import classNames from 'classnames';

import { IGame } from 'src/interface';
import useDeviceSize from 'src/utils/hooks/useDeviceSize';

import { ButtonBorder, ButtonPrimary } from '../Button';
import Chips from '../Chips';
import ImageC from '../Image';
import ModalPlayGame from '../ModalPlayGame';
import Skeleton from '../Skeleton';
import StarRate from '../StarRate';

import classes from './index.module.less';
interface Props {
	type?: 'horizontal' | 'vertical';
	item: IGame;
	isLoading: boolean;
	actionButtons?: any;
}

const CardGame = ({ type = 'vertical', item, isLoading, actionButtons }: Props) => {
	const deviceSize = useDeviceSize();
	const router = useRouter();

	const [openModalPlayGame, setOpenModalPlayGame] = useState<boolean>(false);

	const iconSize = deviceSize !== 'mobile' ? 24 : 18;

	return (
		<>
			<ModalPlayGame
				open={openModalPlayGame}
				onClose={() => setOpenModalPlayGame(false)}
				data={item}
			/>
			<div
				className={classNames(classes['card-game'], classes[type])}
				onClick={() => {
					if (deviceSize === 'mobile' && type === 'horizontal') {
						router.push(`/games/${item?.slug}`);
					}
				}}
			>
				<div className={classes['image-container']}>
					{isLoading ? (
						<Skeleton className={classes.image} />
					) : (
						<div className={classes.image}>
							<ImageC
								alt={`image ${item?.name}`}
								className={classes.img}
								src={item?.main_image_url}
							/>
						</div>
					)}
				</div>
				<div className={classes.info}>
					{isLoading ? <Skeleton height={32} /> : <div className={classes.name}>{item.name}</div>}
					<div className={classes.data}>
						{isLoading ? (
							<div className={classes.chain}>
								<Skeleton variant="text" height={16} width={60} />
								<Skeleton className="mt-1" height={iconSize} width={iconSize} />
							</div>
						) : (
							<div className={classes.chain}>
								<div className={classes.label}>Chain</div>
								<div className="d-flex flex-wrap mt-1" style={{ gap: 6 }}>
									{item?.chains?.map((c) => (
										<ImageC
											alt={`icon ${c.name}`}
											key={c.chain_id}
											width={iconSize}
											height={iconSize}
											src={c.image_url}
										/>
									))}
								</div>
							</div>
						)}
						{isLoading ? (
							<div>
								<Skeleton variant="text" height={16} width={60} />
								<Skeleton className="mt-1" height={iconSize} width={iconSize} />
							</div>
						) : (
							<div>
								<div className={classes.label}>Platform</div>
								<div className="d-flex flex-wrap mt-1" style={{ gap: 10 }}>
									{item?.platform?.toLowerCase() === 'coming soon' ? (
										<span>Coming Soon</span>
									) : (
										item.platform
											?.split(',')
											?.map((p) => (
												<ImageC
													key={p}
													alt={`icon ${p}`}
													width={iconSize}
													height={iconSize}
													src={`/icons/icon-platform-${p.toLowerCase()}.svg`}
												/>
											))
									)}
								</div>
							</div>
						)}
						{isLoading ? (
							<div>
								<Skeleton variant="text" height={16} width={60} />
								<Skeleton className="mt-1" height={iconSize} width={iconSize} />
							</div>
						) : (
							<div>
								<div className={classes.label}>Rating</div>
								{
									<StarRate
										ratingNumber={item.rating}
										className={classNames(classes.rating, 'mt-1')}
									/>
								}
							</div>
						)}

						<div className={classes.category}>
							{isLoading ? (
								<Skeleton variant="text" className="mb-2" height={20} width={90} />
							) : (
								<div className={classes.label}>Category</div>
							)}
							<div className="d-flex flex-wrap" style={{ gap: 10 }}>
								{isLoading
									? new Array(3)
											.fill({ id: 'loading' })
											?.map((_, index) => (
												<Skeleton key={index} className="mb-2" height={26} width={78} />
											))
									: item?.tag_names?.split(', ')?.map((tag, index) => (
											// eslint-disable-next-line react/jsx-indent
											<Chips key={tag + index} className={classes.chips}>
												{tag}
											</Chips>
										))}
							</div>
						</div>
					</div>

					{actionButtons ? (
						<div className={classes.actions}>{actionButtons}</div>
					) : (
						<>
							{(type === 'vertical' || (deviceSize !== 'mobile' && type === 'horizontal')) && (
								<div className={classes.actions}>
									{isLoading ? (
										<Skeleton height={60} borderRadius="4px" />
									) : (
										<React.Fragment>
											{item.status === 'coming-soon' ? (
												<Button className={classes['disabled-game-link']} disabled>
													Coming Soon
												</Button>
											) : (
												<ButtonPrimary
													className={classes.button}
													onClick={() => setOpenModalPlayGame(true)}
												>
													Get Game
												</ButtonPrimary>
											)}
										</React.Fragment>
									)}
									{isLoading ? (
										<Skeleton height={60} borderRadius="4px" />
									) : (
										<Link href={`/games/${item?.slug}`} className={classes['game-link']}>
											<ButtonBorder className={classes.button}>Game Detail</ButtonBorder>
										</Link>
									)}
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</>
	);
};

export default CardGame;
